import React from 'react';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import {action, observable, runInAction, toJS} from 'mobx';
import PropTypes from 'prop-types';

import inject from '../../../../../hoc/injectHoc';
import OnboardUserSigns from '../../../../onboarding/components/onboardUserSigns/OnboardUserSigns';
import OrderDetails from '../../../../onboarding/components/orderDetails/OrderDetails';
import ChargebeeCheckout from '../../../../../common/chargebeeCheckout/ChargebeeCheckout';
import {ALA_CARTE_CREDITS, MEDIA_PLAYER_CREDITS} from '../../../../../../constants/pricingConstants';
import {dashboardRoute} from '../../../../../routePaths';

/**
 * The CheckoutStep component.
 */
class CheckoutStep extends React.Component {
  /**
   * Chargebee Checkout details
   *
   * @type {{openCheckout: boolean, hostedPage: {}, error: boolean}}
   */
  @observable chargebeeData = {
    openCheckout: false,
    hostedPage: null
  }

  /**
   * Chargebee Checkout is Loading
   *
   * @type {boolean}
   */
  @observable isLoadingChargebeeCheckout = false

  /**
   * Flag for if there is an error while loading chargebee checkout
   *
   * @type {boolean}
   */
  @observable hasChargebeeCheckoutError = false;

  /**
   * On change billing type property
   *
   * @param {string} billingType
   */
  onChangeBillingType = (billingType) => {
    const {purchaseFlowStore} = this.props;

    purchaseFlowStore.setBillingType(billingType);
  }

  /**
   * Control the Chargebee Checkout process
   */
  checkoutWithChargebee = async () => {
    const {purchaseFlowStore, apiUserOnboardStore} = this.props;
    const {plan, billingType, alaCarteCredits, mediaPlayerPlan} = purchaseFlowStore;

    const data = {
      subscription: null,
      addons: [],
    };

    if (plan && plan.id) {
      data.subscription = {
        planId: plan.id,
        billingType,
      };
    }

    if (alaCarteCredits) {
      data.addons = [...data.addons, {
        itemId: ALA_CARTE_CREDITS.download.chargebeeId,
        quantity: alaCarteCredits.download
      }, {
        itemId: ALA_CARTE_CREDITS.request.chargebeeId,
        quantity: alaCarteCredits.request
      }];
    }

    if (mediaPlayerPlan) {
      data.addons = [...data.addons, {
        itemId: MEDIA_PLAYER_CREDITS[mediaPlayerPlan.frequency].chargebeeId,
        quantity: mediaPlayerPlan.quantity
      }];
    }

    try {
      runInAction('Set the Chargebee Checkout Loading', () => {
        this.isLoadingChargebeeCheckout = true;

        // reset checkout error
        this.hasChargebeeCheckoutError = false;
      });

      const chargebeeCheckoutResponse = await apiUserOnboardStore.chargebeeCheckout(data);

      runInAction('Set the chargebee hosted page.', () => {
        this.chargebeeData.hostedPage = chargebeeCheckoutResponse;
        this.chargebeeData.openCheckout = true;
      });
    } catch (error) {
      runInAction('Error loading chargebee hosted page.', () => {
        // eslint-disable-next-line no-console
        console.log('Chargebee Error: ', error);
        this.hasChargebeeCheckoutError = true;
      });
    } finally {
      runInAction('Set the Chargebee Checkout Loading', () => {
        this.isLoadingChargebeeCheckout = false;
      });
    }
  }

  @action onSuccessfulChargebeeCheckout = () => {
    const {routerStore, apiUserOnboardStore, apiUserPollMeStore, purchaseFlowStore} = this.props;

    this.chargebeeData.openCheckout = false;

    const planId = purchaseFlowStore.plan ? purchaseFlowStore.plan.id : undefined;
    const payload = toJS({
      planId,
    });

    // call the server onboarding.
    apiUserOnboardStore.clearAll();
    apiUserOnboardStore.makeRequest(payload);
    apiUserPollMeStore.pollThenUpdateMyUser();

    apiUserOnboardStore.getPromise().then(() => {
      routerStore.push(dashboardRoute, {
        replaceIfTo: true,
      });
    });
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {purchaseFlowStore} = this.props;

    const initializeSignWithMediaPlayerDefaults = !!purchaseFlowStore.mediaPlayerPlan;
    const canManageSigns = purchaseFlowStore.plan ? purchaseFlowStore.plan.canManageSigns : true;

    return (
      <div className="row">
        <OnboardUserSigns
          canManageSigns={canManageSigns}
          initializeDefaultSignValues={initializeSignWithMediaPlayerDefaults ? 'mediaPlayerSign' : null}
        />
        {(purchaseFlowStore.plan || purchaseFlowStore.mediaPlayerPlan || purchaseFlowStore.alaCarteCredits) && (
          <OrderDetails
            billingType={purchaseFlowStore.billingType}
            buttonText={'Confirm Purchase'}
            isLoading={this.isLoadingChargebeeCheckout}
            plan={purchaseFlowStore.plan}
            onChangeBillingType={this.onChangeBillingType}
            mediaPlayerPlan={purchaseFlowStore.mediaPlayerPlan}
            alaCarteCredits={purchaseFlowStore.alaCarteCredits}
            onSubmit={this.checkoutWithChargebee}
            userSigns={purchaseFlowStore.userSigns}
          />
        )}
        <ChargebeeCheckout
          openCheckout={this.chargebeeData.openCheckout}
          hostedPage={this.chargebeeData.hostedPage}
          onSuccessfulCheckout={this.onSuccessfulChargebeeCheckout}
        />
      </div>
    );
  }
}

CheckoutStep.propTypes = {
  apiUserOnboardStore: MobxPropTypes.observableObject,
  apiUserPollMeStore: MobxPropTypes.observableObject,
  nextStep: PropTypes.func,
  purchaseFlowStore: MobxPropTypes.observableObject,
  routerStore: MobxPropTypes.observableObject,
};

CheckoutStep.wrappedComponent = {};
CheckoutStep.wrappedComponent.propTypes = {
  apiUserOnboardStore: MobxPropTypes.observableObject.isRequired,
  apiUserPollMeStore: MobxPropTypes.observableObject.isRequired,
  purchaseFlowStore: MobxPropTypes.observableObject.isRequired,
  routerStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(CheckoutStep)(
  observer(CheckoutStep)
);
